import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2, ChevronLeft } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Set to true to re-enable the form
const FORM_ACTIVE = false;

// Helper to publish height to parent iframe
const publishHeight = () => {
  const height = document.documentElement.scrollHeight;
  window.parent.postMessage({ type: 'resize', height }, '*');
};

// Form field schema
const formSchema = {
  slug: "ethos",
  partner: "Ethos",
  topic: "Ethos Contracting Application",
  submitLabel: "Submit",
  questions: [
    { name: "first_name", label: "First name", type: "text", required: true, placeholder: "Brian" },
    { name: "last_name", label: "Last name", type: "text", required: true, placeholder: "Greenberg" },
    { name: "agency_name", label: "Agency or company name", type: "text", required: true, placeholder: "The Insurance Agency Group" },
    { name: "email", label: "Email address", type: "email", required: true, placeholder: "you@agency.com" },
    { name: "phone", label: "Mobile phone", type: "phone", required: true, placeholder: "(555) 555-5555" },
    { name: "ethos_portal_phone", label: "Phone number for Ethos Portal", help: "Must be able to receive text messages.", type: "phone", required: true, placeholder: "(555) 555-5555" },
    { name: "states_licensed", label: "How many states are you licensed in?", type: "number", required: true, placeholder: "e.g. 12" },
    { name: "npn", label: "NPN (National Producer Number)", type: "text", required: true, placeholder: "e.g. 12345678" },
    { name: "website", label: "Your website (if you have one)", type: "url", required: false, placeholder: "youragency.com" },
    { name: "crm", label: "What CRM do you use today?", help: "Leave blank if you don't use one.", type: "text", required: false, placeholder: "e.g. AgencyBloc, Salesforce, GoHighLevel, Radius" },
    { name: "agency_size", label: "How big is your agency?", type: "select", required: true, options: ["Solo producer", "2-5 producers", "6-15 producers", "16-50 producers", "50+ producers"] },
    { name: "annual_revenue", label: "What is your annual agency premium or revenue?", type: "select", required: true, options: ["Pre-revenue / Just getting started", "Under $250K", "$250K - $1M", "$1M - $5M", "$5M - $25M", "$25M+", "Prefer not to say"] },
    { name: "agency_type", label: "What type of agency are you?", type: "select", required: true, options: ["Life insurance only", "Property & casualty (P&C)", "Commercial lines", "Medicare / health", "Multi-line (life + P&C)", "RIA / wealth management", "Bank / credit union / institution", "New to insurance", "Other"] },
    { name: "life_licensed", label: "Are you life licensed?", type: "radio", required: true, options: ["Yes", "No"] },
    { name: "eo_coverage", label: "Do you have $1M in E&O (Errors and Omissions) coverage?", type: "radio", required: true, options: ["Yes", "No"] },
    { name: "quoteplicity_customer", label: "Are you a current or past customer of Quoteplicity?", type: "radio", required: true, options: ["Yes", "No"] },
    { name: "existing_ethos_contract", label: "Do you already have an Ethos contract?", type: "radio", required: true, options: ["No", "Yes"] }
  ]
};

// Phone formatter - formats as you type
function formatPhoneAsYouType(raw) {
  let digits = String(raw || "").replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  } else {
    digits = digits.slice(0, 10);
  }
  if (digits.length === 0) return "";
  if (digits.length < 4) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

// Validators
const validators = {
  email: (value) => {
    if (!value) return "Email address is required.";
    if (value.length > 254) return "Enter a valid email address (e.g. you@agency.com).";
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!regex.test(value)) return "Enter a valid email address (e.g. you@agency.com).";
    return null;
  },
  
  phone: (value) => {
    if (!value) return "Phone number is required.";
    
    // Check format
    const formatRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
    if (!formatRegex.test(value)) {
      return "Enter a phone number in the format (xxx) xxx-xxxx.";
    }
    
    // Extract digits only
    const digits = value.replace(/\D/g, "");
    
    // Check all same digit
    const allSameRegex = /^(\d)\1+$/;
    if (allSameRegex.test(digits)) {
      return "Phone number can't be all the same digit.";
    }
    
    // Check sequential digits
    const ASC = "012345678901234567890";
    const DESC = "098765432109876543210";
    if (ASC.includes(digits) || DESC.includes(digits)) {
      return "Phone number can't be sequential digits.";
    }
    
    return null;
  },
  
  website: (value) => {
    if (!value) return null; // Optional field
    
    let url = value;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }
    
    try {
      const parsed = new URL(url);
      if (!parsed.hostname.includes('.')) {
        return "Enter a valid website (e.g. youragency.com).";
      }
      const tldRegex = /\.[a-z]{2,}$/i;
      if (!tldRegex.test(parsed.hostname)) {
        return "Enter a valid website (e.g. youragency.com).";
      }
      return null;
    } catch {
      return "Enter a valid website (e.g. youragency.com).";
    }
  },
  
  required: (value, label) => {
    if (!value || (typeof value === 'string' && !value.trim())) {
      return `${label} is required.`;
    }
    return null;
  },
  
  number: (value) => {
    if (!value) return "This field is required.";
    const num = parseInt(value, 10);
    if (isNaN(num) || num < 1) {
      return "Enter a valid number.";
    }
    return null;
  }
};

// Submit to backend (stores in DB, then forwards to Zapier)
async function submitApplication(answers) {
  const payload = {
    ...answers,
    source_url: window.location.href,
    user_agent: navigator.userAgent,
  };

  const response = await fetch(`${BACKEND_URL}/api/ethos-contracting`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error("Submission failed");
  }
  
  return response.json();
}

const EthosContractingForm = () => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const formRef = useRef(null);
  const successRef = useRef(null);

  // Set up iframe height communication with parent
  useEffect(() => {
    // Publish initial height after DOM has painted
    requestAnimationFrame(publishHeight);

    // Observe body resize
    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => requestAnimationFrame(publishHeight));
      resizeObserver.observe(document.documentElement);
    }

    // Also publish on window resize
    window.addEventListener('resize', publishHeight);

    return () => {
      window.removeEventListener('resize', publishHeight);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  // Publish height when form state changes
  useEffect(() => {
    const timeout = setTimeout(publishHeight, 100);
    return () => clearTimeout(timeout);
  }, [isSuccess, errors]);

  const validateField = (name, value) => {
    const field = formSchema.questions.find(q => q.name === name);
    if (!field) return null;

    // Type-specific validation
    if (field.type === 'email') {
      return validators.email(value);
    }
    if (field.type === 'phone') {
      return validators.phone(value);
    }
    if (field.type === 'url') {
      return validators.website(value);
    }
    if (field.type === 'number' && field.required) {
      return validators.number(value);
    }
    
    // Required field validation
    if (field.required) {
      return validators.required(value, field.label);
    }
    
    return null;
  };

  const handleChange = (name, value) => {
    // Format phone as you type for any phone-type field
    const field = formSchema.questions.find(q => q.name === name);
    if (field && field.type === 'phone') {
      value = formatPhoneAsYouType(value);
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleBlur = (name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, formData[name]);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const validateAll = () => {
    const newErrors = {};
    let firstErrorField = null;

    formSchema.questions.forEach(field => {
      const error = validateField(field.name, formData[field.name]);
      if (error) {
        newErrors[field.name] = error;
        if (!firstErrorField) {
          firstErrorField = field.name;
        }
      }
    });

    setErrors(newErrors);
    setTouched(
      formSchema.questions.reduce((acc, field) => ({ ...acc, [field.name]: true }), {})
    );

    // Scroll to first error
    if (firstErrorField) {
      const element = document.getElementById(`field-${firstErrorField}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateAll()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await submitApplication(formData);
      setIsSuccess(true);
      
      // Focus the success heading for screen readers
      setTimeout(() => {
        if (successRef.current) {
          successRef.current.focus();
        }
      }, 100);
    } catch (error) {
      console.error('Submission error:', error);
      alert('There was an error submitting your application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field) => {
    const error = touched[field.name] && errors[field.name];
    const errorId = `${field.name}-error`;

    const commonProps = {
      id: `field-${field.name}`,
      'data-testid': `contracting-field-${field.name}`,
      'aria-invalid': error ? 'true' : 'false',
      'aria-describedby': error ? errorId : undefined,
    };

    if (field.type === 'select') {
      return (
        <div key={field.name} className="space-y-2">
          <Label htmlFor={`field-${field.name}`} className="text-sm font-medium text-slate-700">
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </Label>
          <Select
            value={formData[field.name] || ''}
            onValueChange={(value) => handleChange(field.name, value)}
            onOpenChange={() => handleBlur(field.name)}
          >
            <SelectTrigger 
              {...commonProps}
              className={error ? 'border-red-400 focus:ring-red-400' : ''}
            >
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {field.options.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {error && (
            <p id={errorId} className="text-sm text-red-500 mt-1">{error}</p>
          )}
        </div>
      );
    }

    if (field.type === 'radio') {
      return (
        <div key={field.name} className="space-y-2">
          <Label className="text-sm font-medium text-slate-700">
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </Label>
          <div className="flex gap-4 mt-2">
            {field.options.map((option) => (
              <label 
                key={option} 
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="radio"
                  name={field.name}
                  value={option}
                  checked={formData[field.name] === option}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                  onBlur={() => handleBlur(field.name)}
                  className="w-4 h-4 text-emerald-500 border-slate-300 focus:ring-emerald-500"
                  data-testid={`contracting-field-${field.name}-${option.toLowerCase()}`}
                />
                <span className="text-slate-700">{option}</span>
              </label>
            ))}
          </div>
          {error && (
            <p id={errorId} className="text-sm text-red-500 mt-1">{error}</p>
          )}
        </div>
      );
    }

    // Text, email, phone, number, url inputs
    const inputProps = {
      ...commonProps,
      value: formData[field.name] || '',
      onChange: (e) => handleChange(field.name, e.target.value),
      onBlur: () => handleBlur(field.name),
      placeholder: field.placeholder,
      className: error ? 'border-red-400 focus:ring-red-400' : '',
    };

    // Set appropriate input types and modes for mobile
    if (field.type === 'email') {
      inputProps.type = 'email';
      inputProps.autoComplete = 'email';
    } else if (field.type === 'phone') {
      inputProps.type = 'tel';
      inputProps.inputMode = 'numeric';
      inputProps.autoComplete = 'tel';
    } else if (field.type === 'number') {
      inputProps.type = 'number';
      inputProps.inputMode = 'numeric';
      inputProps.min = '1';
    } else if (field.type === 'url') {
      inputProps.type = 'text';
      inputProps.autoComplete = 'url';
    } else {
      inputProps.type = 'text';
    }

    return (
      <div key={field.name} className="space-y-2">
        <Label htmlFor={`field-${field.name}`} className="text-sm font-medium text-slate-700">
          {field.label} {field.required && <span className="text-red-500">*</span>}
        </Label>
        {field.help && (
          <p className="text-xs text-slate-500">{field.help}</p>
        )}
        <Input {...inputProps} />
        {error && (
          <p id={errorId} className="text-sm text-red-500 mt-1">{error}</p>
        )}
      </div>
    );
  };

  // Deactivated screen
  if (!FORM_ACTIVE) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-slate-50 py-12 px-4">
        <div className="max-w-xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center" data-testid="contracting-form-deactivated">
            <h1
              className="text-2xl font-bold text-slate-900 mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Applications are currently closed.
            </h1>
            <p className="text-slate-600">
              We're not accepting new Ethos contracting applications at this time. Have questions? Contact us at{' '}
              <a href="mailto:partners@quoteplicity.com" className="text-emerald-600 hover:underline">
                partners@quoteplicity.com
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success screen
  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-slate-50 py-12 px-4">
        <div className="max-w-xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-xl p-8 text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
              className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            </motion.div>
            
            <h1 
              ref={successRef}
              tabIndex={-1}
              className="text-2xl font-bold text-slate-900 mb-4"
              data-testid="contracting-apply-success"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Got it. Your contracting paperwork is on the way.
            </h1>
            
            <p className="text-slate-600 mb-8">
              Watch your inbox over the next business day for the Ethos contracting forms and a short note on next steps. If you don't see it, check spam or reply to any Quoteplicity email and we'll resend.
            </p>

            <a
              href="/contracting/ethos/"
              className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to Ethos overview
            </a>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-slate-50 py-12 px-4">
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          {/* Ethos Logo */}
          <div className="mb-6">
            <img 
              src="/contracting/ethos-logo.png" 
              alt="Ethos" 
              className="h-12 mx-auto"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
          
          <p className="text-sm font-medium text-emerald-600 uppercase tracking-wide mb-2">
            Ethos Contracting
          </p>
          <h1 
            className="text-3xl font-bold text-slate-900 mb-3"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Apply for Ethos Contracting
          </h1>
          <p className="text-slate-600">
            Answer a few quick questions and we'll send your Ethos contracting paperwork over by email.
          </p>
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-6 sm:p-8"
        >
          <form ref={formRef} noValidate onSubmit={handleSubmit} className="space-y-6">
            {formSchema.questions.map(renderField)}

            <div className="pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="contracting-apply-submit"
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  formSchema.submitLabel
                )}
              </button>
            </div>
          </form>
        </motion.div>

        {/* Footer */}
        <p className="text-center text-slate-500 text-sm mt-6">
          Have questions? Contact us at{' '}
          <a href="mailto:partners@quoteplicity.com" className="text-emerald-600 hover:underline">
            partners@quoteplicity.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default EthosContractingForm;
